export type RowState = "live" | "gone" | "drop" | "needs" | "fault" | "idle";

// Split into colour and weight so stateTextClass can hand a prose caller
// just the hue — StateLabel's own bold is a label-typography choice, not
// part of the state→colour mapping itself.
// `gone` is red because in a restock monitor "you cannot buy this" is the
// single most useful thing a row can tell you, and it read as the quietest
// thing on the card while it was muted grey. It shares the fault hue with a
// broken adapter; the word beside it is what separates "Out of stock" from
// "Store error", which is the rule this file exists to hold.
const TEXT_COLOR: Record<RowState, string> = {
	live: "text-live",
	drop: "text-drop",
	needs: "text-needs",
	fault: "text-fault",
	gone: "text-fault",
	idle: "text-muted",
};

const TEXT_WEIGHT: Record<RowState, string> = {
	live: "font-semibold",
	drop: "font-semibold",
	needs: "font-semibold",
	fault: "font-semibold",
	gone: "font-medium",
	idle: "",
};

// The dot beside the word. Live is foil — the one place a state gets the
// holo sheen, because live is the one state that means "buy it now".
const DOT: Record<RowState, string> = {
	live: "bg-foil",
	drop: "bg-drop",
	needs: "bg-needs",
	fault: "bg-fault",
	gone: "bg-fault/60",
	idle: "bg-line",
};

// The stripe deliberately does NOT follow `gone` into red: most rows in a
// watchlist are out of stock, and a full-height red edge on all of them
// would drown the ones that are genuinely wrong.
const STRIPE: Record<RowState, string> = {
	live: "bg-foil",
	drop: "bg-drop",
	needs: "bg-needs",
	fault: "bg-fault",
	gone: "bg-hairline",
	idle: "bg-hairline",
};

const BORDER_COLOR: Record<RowState, string> = {
	live: "border-live",
	drop: "border-drop",
	needs: "border-needs",
	fault: "border-fault",
	gone: "border-hairline",
	idle: "border-hairline",
};

const DEFAULT_LABEL: Record<RowState, string> = {
	live: "In stock",
	gone: "Gone",
	drop: "Price cut",
	needs: "Needs your tap",
	fault: "Store error",
	idle: "Not checked",
};

/** Left-edge stripe colour for a Row. */
export function stateStripeClass(state: RowState): string {
	return STRIPE[state];
}

/**
 * Just the text colour for a state, for the rare caller that needs the hue
 * without StateLabel's label typography (11px semibold, with a dot). Keeps
 * the state→colour mapping in this one file, which is what the constraint
 * protects — see stateStripeClass, which exists for the same reason.
 */
export function stateTextClass(state: RowState): string {
	return TEXT_COLOR[state];
}

/**
 * Just the border colour for a state, for the rare caller that needs the
 * hue on a border rather than text or a stripe. Keeps the state→colour
 * mapping in this one file — see stateStripeClass and stateTextClass,
 * which exist for the same reason.
 */
export function stateBorderClass(state: RowState): string {
	return BORDER_COLOR[state];
}

/**
 * The state as a word plus its colour. The word is mandatory — state is never
 * signalled by colour alone.
 */
export function StateLabel({ state, label }: { state: RowState; label?: string }) {
	return (
		<span className={`block text-[11px] leading-4 whitespace-nowrap ${TEXT_COLOR[state]} ${TEXT_WEIGHT[state]}`}>
			<span className={`mr-1 inline-block size-1.5 rounded-full align-[0.1em] ${DOT[state]}`} aria-hidden />
			{label ?? DEFAULT_LABEL[state]}
		</span>
	);
}
